import { API_URL, getTokenSmart } from "./api";

interface TTSOptions {
    id: string;
}


/**
 * Requests speech audio for text that `ttsService` has already cleaned up for speaking.
 * Pass `signal` so a stopped or superseded request stops generating on the server.
 */
export const generateSpeech = async (
    text: string,
    options: TTSOptions,
    signal?: AbortSignal
  ): Promise<{
    audio: Blob;
  }> => {
    const sanitizedText = text?.trim() ?? '';

    try {
      if (!sanitizedText) {
        throw new Error("Empty text");
      }
  
      // Get access token if available
      const token = await getTokenSmart();
  
      const ttsRequestBody = {
        text: sanitizedText,
        options: {
          voice: options.id,
        },
      };
  
      const response = await fetch(`${API_URL}/voice/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: "audio/mpeg",
        },
        body: JSON.stringify(ttsRequestBody),
        credentials: "include",
        signal,
      });
  
      // Handle HTTP errors
      if (!response.ok) {
        // Try to parse error details if available
        let errorText: string;
        try {
          errorText = await response.text();
        } catch {
          errorText = "Unknown error";
        }
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }
  
      const contentType = response.headers.get("content-type");
  
      // If backend returns JSON, it's likely an error
      if (contentType && contentType.includes("application/json")) {
        const jsonResponse = await response.json();
        if (jsonResponse.error) {
          throw new Error(jsonResponse.error);
        }
        throw new Error("Unexpected JSON response when expecting audio");
      }
  
      // Ensure we received audio/mpeg
      if (!contentType || !contentType.includes("audio/mpeg")) {
        throw new Error(`Invalid content type: ${contentType}`);
      }
  
      // Get audio data as ArrayBuffer
      const buffer = await response.arrayBuffer();
      if (!buffer || buffer.byteLength === 0) {
        throw new Error("Empty audio data received");
      }
  
      const audioBlob = new Blob([buffer], { type: "audio/mpeg" });
      if (audioBlob.size === 0) {
        throw new Error("Created audio blob is empty");
      }

      return {
        audio: audioBlob,
      };

    } catch (error: any) {
      // Cancellation isn't a failure; keep the AbortError intact so callers can ignore it.
      if (error?.name === "AbortError") {
        throw error;
      }

      console.error('Speech generation error details:', {
        error,
        message: error.message,
        stack: error.stack,
        originalTextLength: text?.length || 0,
        sanitizedTextLength: sanitizedText?.length || 0,
        timestamp: new Date().toISOString()
      });
  
      // Rethrow with more specific error message
      throw new Error(`Speech generation failed: ${error.message}`);
    }
  };